import React, { useEffect, useRef, useState } from 'react';
import { Download, FileSpreadsheet, FileText, Loader2 } from 'lucide-react';
import api from '../../services/api';

// ============================================================
// Botón "Exportar" único de los reportes (#115): abre Excel y PDF, siempre
// en el mismo lugar (al final de la barra de filtros de la cabecera).
//
// Excel, una de dos:
//   excel={() => ({ titulo, detalle, hojas: [{ nombre?, columnas, filas, total? }] })}
//     → POST /exportar/excel arma el .xlsx con el encabezado común (empresa,
//       título, detalle, fecha). Mandar lo que se ve en pantalla, en la
//       moneda elegida. Columnas: { titulo, tipo: texto|monto|entero|porcentaje|fecha }.
//   onExcel={async () => …} → el reporte ya tiene su propio Excel en el backend.
// PDF: por defecto imprime la pantalla (el diálogo permite "Guardar como
//   PDF"); va con <EncabezadoImpresion>. onPdf={async () => …} si el reporte
//   genera su propio PDF en el backend.
// Sin `excel` ni `onExcel` no se ofrece Excel (y lo mismo con pdf={false}).
// ============================================================

export function descargarArchivo(data, nombre) {
  const url = window.URL.createObjectURL(new Blob([data]));
  const link = document.createElement('a');
  link.href = url;
  link.download = nombre;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

function nombreDeRespuesta(headers, porDefecto) {
  const m = /filename="?([^";]+)"?/.exec(headers?.['content-disposition'] || '');
  return m ? m[1] : porDefecto;
}

export async function exportarExcelGenerico(contenido) {
  const res = await api.post('/exportar/excel', contenido, { responseType: 'blob' });
  descargarArchivo(res.data, nombreDeRespuesta(res.headers, 'reporte.xlsx'));
}

export default function Exportar({ excel, onExcel, onPdf, pdf = true, disabled = false }) {
  const [abierto, setAbierto] = useState(false);
  const [trabajando, setTrabajando] = useState(null);
  const caja = useRef(null);
  const conExcel = !!(excel || onExcel);
  const conPdf = pdf !== false;

  useEffect(() => {
    if (!abierto) return undefined;
    const cerrar = (e) => { if (caja.current && !caja.current.contains(e.target)) setAbierto(false); };
    const tecla = (e) => { if (e.key === 'Escape') setAbierto(false); };
    document.addEventListener('mousedown', cerrar);
    document.addEventListener('keydown', tecla);
    return () => { document.removeEventListener('mousedown', cerrar); document.removeEventListener('keydown', tecla); };
  }, [abierto]);

  async function correr(tipo) {
    setAbierto(false);
    if (tipo === 'pdf' && !onPdf) {
      // Deja cerrar el menú antes de abrir el diálogo de impresión.
      setTimeout(() => window.print(), 50);
      return;
    }
    setTrabajando(tipo);
    try {
      if (tipo === 'excel') {
        if (onExcel) await onExcel();
        else await exportarExcelGenerico(await excel());
      } else {
        await onPdf();
      }
    } catch (err) {
      let mensaje = '';
      try { mensaje = err.response?.data instanceof Blob ? JSON.parse(await err.response.data.text()).error : err.response?.data?.error; } catch { /* sin detalle */ }
      alert(mensaje || `No se pudo exportar a ${tipo === 'excel' ? 'Excel' : 'PDF'}`);
    } finally {
      setTrabajando(null);
    }
  }

  if (!conExcel && !conPdf) return null;

  return (
    <div ref={caja} className="relative print:hidden">
      <button
        type="button"
        onClick={() => setAbierto((v) => !v)}
        disabled={disabled || !!trabajando}
        aria-haspopup="menu"
        aria-expanded={abierto}
        className="flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
      >
        {trabajando ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
        Exportar
      </button>
      {abierto && (
        <div role="menu" className="absolute right-0 z-30 mt-1 w-56 rounded-lg border border-slate-200 bg-white py-1 shadow-lg">
          {conExcel && (
            <button type="button" role="menuitem" onClick={() => correr('excel')} className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50">
              <FileSpreadsheet size={17} className="text-green-600" />
              <span>Excel <span className="text-xs text-slate-400">(.xlsx)</span></span>
            </button>
          )}
          {conPdf && (
            <button type="button" role="menuitem" onClick={() => correr('pdf')} className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50">
              <FileText size={17} className="text-red-600" />
              <span>PDF <span className="text-xs text-slate-400">{onPdf ? '(archivo)' : '(imprimir o guardar)'}</span></span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
